/**
 * Article card component
 * @module components/articleCard
 *
 * JavaScript implementation that generates HTML equivalent to existing article-card.liquid and 08_article_primary_tag.liquid
 */

/**
 * Brand tag priority mapping
 * Logic ported from 08_article_primary_tag.liquid
 */
const BRAND_TAG_MAP = [
  'ISSEY MIYAKE',
  'IM MEN',
  '132 5. ISSEY MIYAKE',
  'PLEATS PLEASE ISSEY MIYAKE',
  'HOMME PLISSÉ ISSEY MIYAKE',
  'A-POC ABLE ISSEY MIYAKE',
  'BAO BAO ISSEY MIYAKE',
  'me ISSEY MIYAKE',
  'HaaT',
  'GOOD GOODS ISSEY MIYAKE',
  'IKKO TANAKA ISSEY MIYAKE',
  'ISSEY MIYAKE | MOLESKINE',
  'ISSEY MIYAKE MOLESKINE',  // → 表示は "ISSEY MIYAKE | MOLESKINE"
  'ISSEY MIYAKE PARFUMS',
  'ISSEY MIYAKE WATCH',
  'ISSEY MIYAKE EYES',
  'ISSEY MIYAKE GINZA | CUBE',
  'ISSEY MIYAKE SHINJUKU | SHIKAKU',
  'ISSEY MIYAKE KYOTO | KURA',
  'ISSEY MIYAKE SEMBA | CREATION SPACE',
  'ギャラリー',
  'コーポレート',
  '衣服',
  'バッグ＆アクセサリー',
  '店舗＆その他',
];

/**
 * Retrieve the primary brand name from the tag array.
 * @param {string[]} tags - Array of article tags
 * @returns {string} Brand name (empty string if no match)
 */
export function getPrimaryTag(tags) {
  if (!tags || !Array.isArray(tags)) return '';

  for (const brandTag of BRAND_TAG_MAP) {
    if (tags.includes(brandTag)) {
      // "ISSEY MIYAKE MOLESKINE" は "ISSEY MIYAKE | MOLESKINE" として表示
      if (brandTag === 'ISSEY MIYAKE MOLESKINE') {
        return 'ISSEY MIYAKE | MOLESKINE';
      }
      return brandTag;
    }
  }
  return '';
}

/**
 * Format date
 * @param {string} isoString - ISO 8601 format date string
 * @returns {string} Formatted date (e.g., 2026.01.15)
 */
function formatDate(isoString) {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
}

/**
 * HTML escape
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Truncate string to specified number of characters
 * @param {string} str - Target string
 * @param {number} maxLength - Maximum number of characters
 * @returns {string} Truncated string
 */
function truncate(str, maxLength) {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

/**
 * Generate srcset from Shopify CDN image URL
 * @param {string} url - Image URL
 * @param {number} width - Original image width
 * @returns {string} srcset attribute value
 */
function generateImageSrcset(url, width) {
  if (!url) return '';

  const sizes = [165, 330, 660, 990];
  const srcsetParts = [];

  for (const size of sizes) {
    if (width >= size) {
      // Add/replace size parameter in Shopify CDN URL
      let sizedUrl;
      if (url.includes('width=')) {
        sizedUrl = url.replace(/width=\d+/, `width=${size}`);
      } else if (url.includes('?')) {
        sizedUrl = `${url}&width=${size}`;
      } else {
        sizedUrl = `${url}?width=${size}`;
      }
      srcsetParts.push(`${sizedUrl} ${size}w`);
    }
  }

  // Add original size
  srcsetParts.push(`${url} ${width}w`);

  return srcsetParts.join(', ');
}

/**
 * Extract numeric ID from Shopify-formatted ID
 * @param {string} gid - GraphQL ID (gid://shopify/Article/123456)
 * @returns {string} Numeric ID
 */
function extractArticleId(gid) {
  if (!gid) return '';
  const match = gid.match(/Article\/(\d+)/);
  return match ? match[1] : '';
}

/**
 * Generate HTML for an article card.
 * Outputs HTML structure equivalent to article-card.liquid.
 *
 * @param {Object} article - Article object from GraphQL API
 * @param {string} article.id - Article ID (gid://shopify/Article/xxx)
 * @param {string} article.title - Article title
 * @param {string} article.handle - Article handle
 * @param {string} article.publishedAt - Publication date (ISO 8601)
 * @param {string[]} article.tags - Array of tags
 * @param {Object} [article.image] - Article image
 * @param {Object} article.blog - Parent blog
 * @param {string} [article.onlineStoreUrl] - Article URL
 * @param {Object} [article.metafield] - Metafield (img_border)
 * @param {string} [additionalClass=''] - Additional CSS class (js-news__invisible, etc.)
 * @returns {string} HTML string
 */
export function renderArticleCard(article, additionalClass = '') {
  const articleId = extractArticleId(article.id);
  const title = escapeHtml(truncate(article.title, 50));
  const publishedDate = formatDate(article.publishedAt);
  const brandName = getPrimaryTag(article.tags);
  const articleUrl = article.onlineStoreUrl
    || `/blogs/${article.blog.handle}/${article.handle}`;

  // Image part
  const hasImage = !!article.image?.url;
  let imageHtml = '';
  if (hasImage) {
    const imageUrl = article.image.url;
    const imageAlt = escapeHtml(article.image.altText || article.title);
    const imageWidth = article.image.width || 330;
    const imageHeight = article.image.height || 220;
    const srcset = generateImageSrcset(imageUrl, imageWidth);

    // Generate default src URL for display
    const defaultSrc = imageUrl.includes('?')
      ? `${imageUrl}&width=330`
      : `${imageUrl}?width=330`;

    imageHtml = `
    <img
      srcset="${srcset}"
      src="${defaultSrc}"
      sizes="(min-width: 1440px) 330px, (min-width: 768px) 33vw, 26vw"
      alt="${imageAlt}"
      class="motion-reduce"
      loading="lazy"
      width="${imageWidth}"
      height="${imageHeight}"
    >`;
  }

  // Build class names (same structure as existing article-card.liquid)
  const cellClasses = ['news', '_cell', `article_id_${articleId}`];

  // Add type-border class if metafield img_border is "true"
  if (article.metafield?.value === 'true') {
    cellClasses.push('type-border');
  }

  if (additionalClass) {
    cellClasses.push(additionalClass);
  }

  const imageClass = hasImage ? '_image' : '_image _image--no-img';

  return `
<a href="${articleUrl}" class="${cellClasses.join(' ')}">
  <div class="${imageClass}">
    ${imageHtml}
  </div>
  <div class="news__text _detail">
    <p class="_brand">${escapeHtml(brandName)}</p>
    <p class="_text">${title}</p>
    <p class="_date">${publishedDate}</p>
  </div>
</a>`;
}

/**
 * Generate HTML from array of articles.
 * @param {Object[]} articles - Array of article objects
 * @param {number} [visibleCount=8] - Number of articles to display (excess will get js-news__invisible class)
 * @returns {string} HTML string
 */
export function renderArticleCards(articles, visibleCount = 8) {
  return articles.map((article, index) => {
    const additionalClass = index >= visibleCount ? 'js-news__invisible' : '';
    return renderArticleCard(article, additionalClass);
  }).join('');
}
